import React, { useEffect, useState } from "react";
import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useSwitchChain, useWalletClient } from "wagmi";
import { getWalletClient } from "wagmi/actions";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { config } from "../..";
import { useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { broadcastClaimRewardsTx } from "../../helpers/claimRewards";

const chainIcon = (chainId: number) =>
  chainId === 42161 ? "/external/arbitrum_icon2.png" : "/external/base_icon2.png";

/**
 * Small dialog shown only when the user has relevant funds on BOTH chains
 * (staked balance for unstake, pending rewards for claim). It lists only the
 * chains with something relevant and continues the flow on the chosen chain.
 */
const SelectNetworkDialog: React.FC = () => {
  const { setSelectedChainId, setTransactionDetails, updatePointsAndRewards } =
    useGlobalState();
  const {
    isSelectNetworkDialogVisible,
    selectNetworkOptions,
    selectNetworkPurpose,
    closeSelectNetworkDialog,
    showStakeModal,
    setClaimBroadcastModalVisible,
    showErrorMessage,
  } = useModalState();

  const { switchChainAsync } = useSwitchChain({ config });
  const { data: walletClient } = useWalletClient();
  const { openConnectModal } = useConnectModal();

  const [selectedChain, setSelectedChain] = useState<8453 | 42161 | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  // Reset selection whenever the dialog opens
  useEffect(() => {
    if (isSelectNetworkDialogVisible) {
      setSelectedChain(null);
      setIsSwitching(false);
    }
  }, [isSelectNetworkDialogVisible]);

  const amountCaption =
    selectNetworkPurpose === "claim" ? "Pending rewards" : "Staked balance";

  // Await the network switch before continuing: firing switch + broadcast in
  // the same tick races the wallet's switch prompt against the tx prompt, and
  // the pre-switch walletClient would read the rewards contract on the wrong
  // chain (bogus withdraw amount / reverted tx).
  const handleContinue = async () => {
    if (selectedChain == null || isSwitching) return;
    setIsSwitching(true);
    try {
      await switchChainAsync({ chainId: selectedChain });
    } catch {
      // User rejected the network switch — keep the dialog open so they can
      // pick again or cancel.
      setIsSwitching(false);
      return;
    }
    setSelectedChainId(selectedChain);
    closeSelectNetworkDialog();

    if (selectNetworkPurpose === "claim") {
      // Re-resolve the wallet client on the new chain; the hook value in this
      // closure still points at the pre-switch chain.
      const freshClient =
        (await getWalletClient(config, { chainId: selectedChain }).catch(() => null)) ?? walletClient;
      // Only open the broadcast modal once a tx actually went out.
      // broadcastClaimRewardsTx early-returns (no wallet / user rejects)
      // without setting transactionDetails; opening the modal then would leave
      // its visible flag stuck true with nothing to render.
      const txHash = await broadcastClaimRewardsTx(freshClient, selectedChain, {
        setTransactionDetails,
        updatePointsAndRewards,
        showErrorMessage,
        openConnectModal,
      });
      if (txHash) setClaimBroadcastModalVisible(true);
    } else {
      showStakeModal();
    }
  };

  return (
    <DialogRoot
      lazyMount
      size="sm"
      open={isSelectNetworkDialogVisible}
      onOpenChange={(e) => {
        if (!e.open) closeSelectNetworkDialog();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select network</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={3}>
            {selectNetworkOptions.map((option) => {
              const isSelected = selectedChain === option.chainId;
              return (
                <Flex
                  key={option.chainId}
                  as="button"
                  width="full"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={3}
                  p={4}
                  bg={isSelected ? "#faf5ff" : "#f4f4f5"}
                  borderWidth="1px"
                  borderColor={isSelected ? "#9333ea" : "#e4e4e7"}
                  borderRadius="12px"
                  cursor="pointer"
                  transition="border-color 150ms, background 150ms"
                  _hover={{ borderColor: isSelected ? "#9333ea" : "#a1a1aa" }}
                  onClick={() => setSelectedChain(option.chainId)}
                >
                  <Flex alignItems="center" gap={3}>
                    <img
                      src={chainIcon(option.chainId)}
                      alt={option.name}
                      style={{ width: 24, height: 24 }}
                    />
                    <Text fontWeight={600} color="#18181b">
                      {option.name}
                    </Text>
                  </Flex>
                  <Box textAlign="right">
                    <Text fontWeight={500} color="#18181b">
                      {option.amount.toFixed(4)} MOR
                    </Text>
                    <Text fontSize="xs" color="#a1a1aa">
                      {amountCaption}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={closeSelectNetworkDialog}>
            Cancel
          </Button>
          <Button
            colorPalette="purple"
            bg="#9333ea"
            disabled={selectedChain == null}
            loading={isSwitching}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default SelectNetworkDialog;
